import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { getBaseUrl } from '@plone/volto/helpers';
import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';

type SearchVariant = 'desktop' | 'bar';

interface SearchFormProps {
  /*
  `desktop` é o campo fixo da barra azul a partir de 1024px; `bar` é o campo
  que a lupa abre no lugar dela abaixo desse ponto. Os dois convivem no DOM
  (um deles apenas oculto), então cada um tem o próprio `id`.
  */
  variant: SearchVariant;
  /* Leva o foco ao campo assim que ele entra — usado pela variante `bar`.
     Nome próprio, e não o atributo `autoFocus` do DOM: só assim dá para pedir
     `preventScroll`, e o foco fica restrito ao campo que a lupa acabou de
     abrir (o atributo roubaria o foco também no carregamento da página). */
  focusOnMount?: boolean;
}

const VARIANTS: Record<
  SearchVariant,
  { className: string; inputId: string; ariaLabel: string }
> = {
  desktop: {
    className: 'mpf-topbar__search',
    inputId: 'site-search',
    ariaLabel: 'Buscar no portal',
  },
  bar: {
    className: 'mpf-topbar__search mpf-topbar__search--bar',
    inputId: 'site-search-bar',
    ariaLabel: 'Buscar no site',
  },
};

export const SearchForm: React.FC<SearchFormProps> = ({
  variant,
  focusOnMount = false,
}) => {
  const [search, setSearch] = useState('');
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);

  const { className, inputId, ariaLabel } = VARIANTS[variant];

  // O foco é dado por ref, e não pelo atributo `autoFocus`: só assim dá para
  // pedir `preventScroll` — o campo nasce colado no header e rolar até ele
  // faria a página pular.
  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus({ preventScroll: true });
  }, [focusOnMount]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (search.length > 0) {
      const url = `${getBaseUrl(
        '/',
      )}/search?SearchableText=${encodeURIComponent(search)}`;
      history.push(url);
      setSearch('');
    }
  };

  return (
    <form
      className={className}
      role="search"
      onSubmit={onSubmit}
      action="./search"
    >
      <label className="sr-only" htmlFor={inputId}>
        Buscar
      </label>
      {/* Campo e botão dividem a mesma moldura arredondada. */}
      <div className="mpf-topbar__search-field">
        <input
          ref={inputRef}
          id={inputId}
          name="SearchableText"
          type="search"
          placeholder="O que você procura?"
          aria-label={ariaLabel}
          className="mpf-topbar__search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <MPFIconButton
          type="submit"
          variant="unstyled"
          className="mpf-topbar__search-submit"
          data-tooltip="Buscar"
          icon={<MagnifyingGlassIcon className="mpf-topbar__search-icon" />}
          label="Buscar"
        />
      </div>
    </form>
  );
};

export default SearchForm;
